import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VdoPlayer from '../Lesson/VdoPlayer.tsx';
import { Box, Button, Text, Flex, Container, Table, TextArea } from '@radix-ui/themes';
import api from '../../api/axios.js';
import './EditLesson.css';

const EditLesson = () => {
    const { id } = useParams();  // Get the id from URL
    const navigate = useNavigate();
    const [lesson, setLesson] = useState(null);
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [editedDescription, setEditedDescription] = useState('');
    const [videoFile, setVideoFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState('');
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchLessonAndExams();
    }, [id]);

    const fetchLessonAndExams = async () => {
        try {
            // Fetch lesson details
            const lessonResponse = await api.get(`/lessons/${id}`);
            console.log('Lesson data:', lessonResponse.data);
            setLesson(lessonResponse.data);
            setEditedName(lessonResponse.data.name);
            setEditedDescription(lessonResponse.data.description);
            setVideoPreview(lessonResponse.data.vdo_file);

            // Fetch related exams
            const examsResponse = await api.get(`/allExams/lesson/${id}`);
            setExams(examsResponse.data);

            setLoading(false);
        } catch (err) {
            console.error('Error fetching lesson:', err);
            setError(err.message);
            setLoading(false);
        }
    };

    const handleVideoChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('video/')) {
            setVideoFile(file);
            setVideoPreview(URL.createObjectURL(file));
        } else {
            alert('Please select a valid video file');
        }
    };

    const handleSave = async () => {
        if (!editedName.trim()) {
            alert('Please enter a lesson name');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('title', editedName);
            formData.append('description', editedDescription);
            formData.append('isEdit', 'true');  // Flag to indicate this is an edit
            formData.append('lessonId', id);    // Send the lesson ID for editing
            
            if (videoFile) {
                formData.append('video', videoFile);
            }

            // Use the same endpoint for both new uploads and edits
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/uploadVdo`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to update lesson');
            }

            setIsEditing(false);
            setVideoFile(null);
            fetchLessonAndExams(); // Refresh the data
        } catch (error) {
            console.error('Error updating lesson:', error);
            alert('Error updating lesson: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedName(lesson.name);
        setEditedDescription(lesson.description);
        setVideoFile(null);
        setVideoPreview(lesson.vdo_file);
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!lesson) return <div>Lesson not found</div>;

    return (
        <Container size="2">
            <Box style={{ padding: '2rem' }}>
                <Flex justify="between" align="center" mb="4">
                    <Text size="6" weight="bold">แก้ไขบทเรียน</Text>
                    <Flex gap="2">
                        <Button onClick={() => navigate('/backend/lessons')} style={{ cursor: 'pointer' }}>
                            กลับไปบทเรียนทั้งหมด
                        </Button>
                        <Button onClick={() => navigate(`/backend/uploadExam/${lesson.id}`)} style={{ cursor: 'pointer' }}>
                            {exams.length > 0 ? 'แก้ไขข้อสอบ' : 'อัปโหลดข้อสอบ'}
                        </Button>
                    </Flex>
                </Flex>

                <div className='lesson-item'>
                    <VdoPlayer videoUrl={isEditing ? videoPreview : lesson.vdo_file} />
                    <Box mt="4" align="left">
                        {isEditing ? (
                            <Box className="edit-form">
                                <input
                                    type="text"
                                    className="radix-text-input"
                                    placeholder="Lesson Name"
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                />
                                <TextArea
                                    className="radix-textarea"
                                    placeholder="Description"
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                />
                                <Box className="file-upload-container">
                                    <Text size="2" mb="2" color="gray">เลือกวิดีโอใหม่</Text>
                                    <input
                                        type="file"
                                        accept="video/*"
                                        onChange={handleVideoChange}
                                        className="radix-file-input"
                                    />
                                </Box>
                                <Flex gap="3" mt="4">
                                    <Button 
                                        onClick={handleSave}
                                        disabled={uploading}
                                    >
                                        {uploading ? 'กำลังบันทึก...' : 'บันทึกการเปลี่ยนแปลง'}
                                    </Button>
                                    <Button 
                                        variant="soft" 
                                        color="gray" 
                                        onClick={handleCancel}
                                        disabled={uploading}
                                    >
                                        ยกเลิก
                                    </Button>
                                </Flex>
                            </Box>
                        ) : (
                            <>
                                <Flex justify="between" align="center" mb="2">
                                    <Text size="5" weight="bold">
                                        {lesson.name}
                                    </Text>
                                    <Button 
                                        variant="soft" 
                                        onClick={() => setIsEditing(true)}
                                    >
                                        Edit
                                    </Button>
                                </Flex>
                                <Text size="3" color="gray">
                                    {lesson.description}
                                </Text>
                            </>
                        )}
                    </Box>

                    {/* Exams Table */}
                    <Box mt="6">
                        <Text size="5" weight="bold" mb="4">ข้อสอบ</Text>
                        {exams.length > 0 ? (
                            <Table.Root>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.ColumnHeaderCell>No.</Table.ColumnHeaderCell>
                                        <Table.ColumnHeaderCell>Question & Choice</Table.ColumnHeaderCell>
                                    </Table.Row>
                                </Table.Header>

                                <Table.Body>    
                                    {exams.map((exam, index) => (
                                        <React.Fragment key={exam.id}>
                                        <Table.Row>
                                            <Table.Cell>{index + 1}</Table.Cell>
                                            <Table.Cell>{exam.question}</Table.Cell>
                                        </Table.Row>
                                        <Table.Row>
                                            <Table.Cell></Table.Cell>
                                            <Table.Cell>
                                                <Flex direction="column" gap="2">
                                                    <Text>1. {exam.choice1}</Text>
                                                    <Text>2. {exam.choice2}</Text>
                                                    <Text>3. {exam.choice3}</Text>
                                                    <Text>4. {exam.choice4}</Text>
                                                    <Text>5. {exam.choice5}</Text>
                                                    <Text>6. {exam.choice6}</Text>
                                                    <Text weight="bold">Answer: {exam.answer}</Text>
                                                </Flex>
                                            </Table.Cell>
                                        </Table.Row>
                                    </React.Fragment>
                                    ))}
                                </Table.Body>
                            </Table.Root>
                        ) : (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Text color="gray">ไม่พบข้อสอบสำหรับบทเรียนนี้</Text>
                            </div>
                        )}
                    </Box>
                </div>
            </Box>
        </Container>
    );
};

export default EditLesson;